import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { RefreshButton } from '../hooks/useRefreshTrigger';

const fmtNZT = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

// ── Resolution modal ──────────────────────────────────────────────────────────
const ResolutionModal = ({ row, onConfirm, onCancel }) => {
  const [note, setNote]             = useState('');
  const [resolution, setResolution] = useState(null);

  if (!row) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 8, padding: 24, width: 480, maxWidth: '90vw',
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', marginBottom: 8 }}>
          Resolve Discrepancy
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
          {row.order_ref} — {row.item_code} ({row.item_name})
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button
            className={`action-btn ${resolution === 'fixed' ? 'action-btn--close' : 'action-btn--ghost'}`}
            onClick={() => setResolution('fixed')}
            style={{ flex: 1, padding: '8px 0' }}
          >
            ✓ Fixed
          </button>
          <button
            className={`action-btn ${resolution === 'ignored' ? 'action-btn--active' : 'action-btn--ghost'}`}
            onClick={() => setResolution('ignored')}
            style={{ flex: 1, padding: '8px 0' }}
          >
            ✕ Ignore
          </button>
        </div>

        <textarea
          placeholder="Add a note (optional) — e.g. added product option manually, duplicate line..."
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
          style={{
            width: '100%', background: 'var(--bg)', border: '1px solid var(--border2)',
            borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-body)',
            fontSize: 12, padding: '8px 10px', resize: 'vertical', marginBottom: 16,
            boxSizing: 'border-box',
          }}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="action-btn action-btn--ghost" onClick={onCancel}>Cancel</button>
          <button
            className="action-btn action-btn--close"
            disabled={!resolution}
            onClick={() => onConfirm(row.id, resolution, note)}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const ProductErrorManagement = () => {
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [resolving, setResolving]     = useState(null);
  const [search, setSearch]           = useState('');
  const [rechecking, setRechecking]   = useState(null);

  const load = useCallback(async (resolved = false) => {
    setLoading(true);
    let query = supabase
      .from('product_error_management')
      .select('id, order_id, order_ref, item_code, item_name, internal_comments, created_date, status, resolved_by, resolved_at, resolution_note, first_seen_at')
      .order('first_seen_at', { ascending: false });

    if (!resolved) query = query.eq('status', 'open');

    const { data } = await query;
    setRows(data || []);
    setLoading(false);
  }, []);

  const handleResolveConfirm = async (id, resolution, note) => {
    await supabase
      .from('product_error_management')
      .update({
        status:          resolution,
        resolved_by:     resolution,
        resolution_note: note || null,
        resolved_at:     new Date().toISOString(),
      })
      .eq('id', id);
    if (showResolved) {
      setRows(prev => prev.map(r =>
        r.id === id ? { ...r, status: resolution, resolved_by: resolution, resolution_note: note || null } : r
      ));
    } else {
      setRows(prev => prev.filter(r => r.id !== id));
    }
    setResolving(null);
  };

  const handleRecheck = async (row) => {
    setRechecking(row.id);
    const { data } = await supabase
      .from('product_options')
      .select('code')
      .eq('code', row.item_code);
    setRechecking(null);
    if (data && data.length) {
      alert(`✓ ${row.item_code} now exists in product_options.`);
    } else {
      alert(`✕ ${row.item_code} still not found in product_options.`);
    }
  };

  const handleToggleResolved = () => {
    const next = !showResolved;
    setShowResolved(next);
    load(next);
  };

  useEffect(() => { load(false); }, [load]);

  const filteredRows = rows.filter(row => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const haystack = [row.order_ref, row.item_code, row.item_name].join(' ').toLowerCase();
    return haystack.includes(q);
  });

  const openCount = rows.filter(r => r.status === 'open').length;

  return (
    <main className="app-main">
      <div style={{ padding: '24px 32px' }}>
        <div className="dash-panel">
          <div className="dash-panel__header">
            <span className="dash-panel__title">
              🏷️ Product Error Management
              {openCount > 0 && (
                <span style={{
                  marginLeft: 8, background: '#f87171', color: '#fff',
                  borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700,
                }}>
                  {openCount} open
                </span>
              )}
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                className="action-btn action-btn--ghost"
                style={{ fontSize: 10, padding: '2px 10px' }}
                onClick={handleToggleResolved}
              >
                {showResolved ? 'Hide Fixed/Ignored' : 'Show Fixed/Ignored'}
              </button>
              {/* Re-runs the daily check on demand. The Operational Events
                  warning reflects the last run's verdict, so fixing rows here
                  does not clear it until the check runs again -- otherwise it
                  stays red until 22:00 even with nothing outstanding. */}
              <RefreshButton
                triggerKey="missing_product_check"
                label="Re-run Check"
                onDone={() => load(showResolved)}
              />
              <button className="dash-refresh-btn" onClick={() => load(showResolved)} disabled={loading}>↻</button>
            </div>
          </div>

          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            padding: '10px 16px', borderBottom: '1px solid var(--border)',
          }}>
            <input
              type="text"
              placeholder="Search order ref or item code…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                flex: 1, background: 'var(--bg)', border: '1px solid var(--border2)',
                borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-body)',
                fontSize: 12, padding: '5px 10px', outline: 'none',
              }}
            />
          </div>

          {loading ? <div className="dash-loading">Loading…</div> :
           !filteredRows.length ? (
            <div className="dash-empty dash-empty--ok">
              {rows.length ? '✓ No rows match search' : '✓ No open product code discrepancies'}
            </div>
          ) : (
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Order Ref</th>
                  <th>Item Code</th>
                  <th>Item Name</th>
                  <th>Internal Comments</th>
                  <th>First Seen</th>
                  <th style={{ width: 110 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id} className={row.status === 'open' ? 'dash-row--action' : ''}>
                    <td>{row.order_ref}</td>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{row.item_code}</td>
                    <td>{row.item_name}</td>
                    <td className="muted">
                      {row.internal_comments || ''}
                      {row.resolution_note && (
                        <div style={{ fontSize: 10, color: 'var(--gold)', marginTop: 2, fontStyle: 'italic' }}>
                          Note: {row.resolution_note}
                        </div>
                      )}
                    </td>
                    <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: 11 }}>
                      {fmtNZT(row.first_seen_at)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {row.status === 'open' ? (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            className="action-btn action-btn--ghost"
                            style={{ fontSize: 10, padding: '2px 10px' }}
                            disabled={rechecking === row.id}
                            onClick={() => handleRecheck(row)}
                          >
                            {rechecking === row.id ? 'Checking…' : 'Recheck'}
                          </button>
                          <button
                            className="action-btn action-btn--ghost"
                            style={{ fontSize: 10, padding: '2px 10px' }}
                            onClick={() => setResolving(row)}
                          >
                            Resolve ▾
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: 10, color: row.status === 'fixed' ? 'var(--green-light)' : 'var(--muted)' }}>
                          {row.status === 'fixed' ? '✓ Fixed' : '✕ Ignored'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ResolutionModal
        row={resolving}
        onConfirm={handleResolveConfirm}
        onCancel={() => setResolving(null)}
      />
    </main>
  );
};

export default ProductErrorManagement;
